import { ReactElement } from 'react';

/* tslint:disable:max-line-length */

interface ModerationPolicyContentProps {
  styles: {
    legals: string;
    ol: string;
    ul?: string;
  };
}

export function ModerationPolicyContent({ styles }: ModerationPolicyContentProps): ReactElement {
  return (
    <div className={styles.legals}>
      <h1 className="slds-text-heading--large slds-m-bottom--large">User-Generated Content Moderation Policy</h1>
      <p>Last updated: February 4, 2026</p>

      <h2>1. Purpose and Scope</h2>

      <p>
        RaiderIO ("RaiderIO," "we," "us") provides limited areas where users can submit or display user-generated
        content ("UGC"). This User-Generated Content Moderation Policy ("Policy") describes:
      </p>

      <ul className={styles.ol}>
        <li>where UGC exists on the RaiderIO website and related services (the "Site"),</li>
        <li>the rules that apply to UGC ("Community Guidelines"),</li>
        <li>how we moderate and enforce those rules, and</li>
        <li>how users and visitors can report content or appeal moderation actions.</li>
      </ul>

      <p>
        This Policy applies to all UGC submitted, published, sent, or otherwise made available through the Site now or
        in the future. This Policy is intended to be read alongside our Terms of Service and Privacy Policy. If this
        Policy conflicts with our Terms of Service, the Terms of Service control.
      </p>

      <p>
        We strive to comply with applicable laws and regulations in connection with UGC moderation. Nothing in this
        Policy creates an obligation to monitor all UGC or to take action in every instance.
      </p>

      <h2>2. Where UGC Exists on RaiderIO</h2>

      <p>
        UGC on RaiderIO is limited and primarily game-related. Depending on the feature, UGC may be publicly visible,
        visible to logged-in users, or private between users.
      </p>

      <p>UGC may appear in areas including (but not limited to):</p>

      <ol className={styles.ol}>
        <li>
          <strong>Profile and biography fields (public)</strong>
          <br />
          Limited text fields on character, guild, team, and user pages (for example: short bios, descriptions, or
          similar text).
          <ul className={styles.ol}>
            <li>
              Some areas may allow a restricted set of allow-listed images (e.g., game-related imagery). RaiderIO does
              not allow unrestricted user image uploads in these locations.
            </li>
          </ul>
        </li>
        <li>
          <strong>Team and group features (public)</strong>
          <br />
          Custom teams and related group listings that users create and manage on the Site, including content that may
          appear on leaderboards and team pages.
        </li>
        <li>
          <strong>Recruitment features (public)</strong>
          <br />
          Recruitment profiles and listings for characters, teams, and guilds, including short user-provided text such
          as captions or descriptions that may appear in search results, tooltips, or other recruitment surfaces.
        </li>
        <li>
          <strong>Recruitment messaging (private)</strong>
          <br />
          On-site messaging between users who have established a recruitment connection (for example, applicants and
          recruiters). Messages are intended to be private between the participants.
        </li>
        <li>
          <strong>Other interactive or customizable features (varies)</strong>
          <br />
          Any future interactive features where users can submit, publish, or share content through the Site.
        </li>
      </ol>

      <h3>Third-party game data</h3>

      <p>
        Certain game identifiers displayed on RaiderIO—such as World of Warcraft character names and guild
        names—originate from Blizzard Entertainment systems and are subject to Blizzard's naming rules and moderation at
        the source. RaiderIO displays these identifiers as part of game data and does not create them. However, we may
        take action on our Site (for example, limiting visibility) where feasible when we become aware of content that
        violates this Policy.
      </p>

      <h2>3. Community Guidelines</h2>

      <p>
        UGC must be appropriate for a general audience and must not be unlawful, harmful, or otherwise objectionable.
        The following categories of content are prohibited in any RaiderIO UGC area (public or private):
      </p>

      <h3>3.1 Illegal content and harmful conduct</h3>
      <ul className={styles.ol}>
        <li>Content that violates applicable law or encourages, promotes, or facilitates illegal activity.</li>
        <li>Instructions for wrongdoing, scams, fraud, or deceptive practices.</li>
        <li>Content that facilitates theft, exploitation, or abuse.</li>
      </ul>

      <h3>3.2 Child safety</h3>
      <ul className={styles.ol}>
        <li>
          Any sexual content involving minors, child sexual abuse material (CSAM), or content that exploits or endangers
          minors.
        </li>
      </ul>
      <p>
        We may report apparent CSAM or child exploitation to appropriate authorities and/or relevant organizations, as
        required or appropriate.
      </p>

      <h3>3.3 Hate, harassment, and threats</h3>
      <ul className={styles.ol}>
        <li>
          Hate speech, hateful symbols, or content that targets a protected class (including race, ethnicity,
          nationality, religion, gender identity, sexual orientation, disability, or similar protected characteristics).
        </li>
        <li>Harassment, bullying, intimidation, or stalking.</li>
        <li>Threats of violence or encouragement of harm against any person or group.</li>
      </ul>

      <h3>3.4 Sexual and explicit content</h3>
      <ul className={styles.ol}>
        <li>Pornographic or sexually explicit content, including explicit sexual acts.</li>
        <li>Sexually exploitative content or solicitation.</li>
        <li>Nudity or sexual content that is not appropriate for a general-audience environment.</li>
      </ul>

      <h3>3.5 Violence and self-harm</h3>
      <ul className={styles.ol}>
        <li>Graphic violence or gore.</li>
        <li>Content that encourages or glorifies self-harm, suicide, or eating disorders.</li>
      </ul>

      <h3>3.6 Privacy and personal data</h3>
      <ul className={styles.ol}>
        <li>
          Posting or sharing someone else's personal information without permission ("doxxing"), including addresses,
          phone numbers, email addresses, government IDs, financial details, or other sensitive data.
        </li>
        <li>Sharing private communications, personal photos, or private details without consent.</li>
      </ul>

      <h3>3.7 Impersonation and misrepresentation</h3>
      <ul className={styles.ol}>
        <li>Impersonating RaiderIO staff, another user, or an organization in a misleading way.</li>
        <li>Misrepresenting affiliation or authority to deceive others.</li>
      </ul>

      <h3>3.8 Spam, commercial solicitation, and advertising abuse</h3>
      <ul className={styles.ol}>
        <li>Spam, repetitive posting, or disruptive behavior.</li>
        <li>
          Unapproved commercial advertising or solicitation, including affiliate promotion, link farming, and bulk
          outreach.
        </li>
      </ul>
      <p>
        (For clarity: legitimate in-feature recruitment information is allowed when used as intended and when it
        complies with this Policy.)
      </p>

      <h3>3.9 Security and platform abuse</h3>
      <ul className={styles.ol}>
        <li>Malware, phishing, credential harvesting, or malicious links.</li>
        <li>Attempts to bypass filters, exploit the Site, or interfere with Site operations.</li>
      </ul>

      <h3>3.10 Intellectual property and rights violations</h3>
      <ul className={styles.ol}>
        <li>Content that infringes copyright, trademark, or other intellectual property rights.</li>
        <li>Unauthorized use of another person's content or likeness in a manner that violates their rights.</li>
      </ul>

      <h3>3.11 Game policy violations</h3>
      <ul className={styles.ol}>
        <li>
          Content that violates Blizzard's World of Warcraft Terms of Use or related policies, including real-money
          trading (RMT) advertisements or services that breach game rules.
        </li>
      </ul>

      <h3>3.12 "Otherwise objectionable" content and brand safety</h3>
      <p>
        We may also restrict or remove content that is not illegal but is inappropriate for a general audience, creates
        a meaningful safety or brand-safety risk, or undermines the integrity of the Site (for example: extreme
        profanity directed at individuals, content designed to shock, or other content that a reasonable person would
        consider objectionable).
      </p>

      <h2>4. How RaiderIO Moderates UGC</h2>

      <p>
        RaiderIO uses a combination of product controls, automated systems, and human review. Our approach is designed
        to reduce the risk of harmful content while recognizing that not all content can be reviewed before it appears.
      </p>

      <h3>4.1 Product and technical controls</h3>
      <p>Depending on the feature, we may use controls such as:</p>
      <ul className={styles.ol}>
        <li>field length limits and structured inputs,</li>
        <li>restrictions on image use (including allow-listed images where applicable),</li>
        <li>spam and abuse detection,</li>
        <li>rate limits or other anti-abuse measures, and</li>
        <li>automated filtering intended to prevent or reduce egregious content.</li>
      </ul>

      <h3>4.2 Automated and manual review</h3>
      <ul className={styles.ol}>
        <li>Automated systems may block or limit certain content before publishing or displaying it.</li>
        <li>Manual review may occur in response to user reports, internal flags, or proactive checks.</li>
        <li>We do not guarantee that all UGC is screened before it appears.</li>
      </ul>

      <h3>4.3 Private messaging</h3>
      <p>
        Recruitment messages are intended to be private between participants. We do not commit to routine pre-screening
        of private messages. However, we may review private messages:
      </p>
      <ul className={styles.ol}>
        <li>when content is reported,</li>
        <li>when investigating abuse, spam, or security issues, or</li>
        <li>as otherwise necessary to enforce this Policy, protect users, or comply with law.</li>
      </ul>

      <h2>5. Reporting UGC Violations</h2>

      <p>
        We maintain a process for users and visitors to report alleged violations of this Policy or raise related
        concerns.
      </p>

      <p>
        To report content, email <a href="mailto:moderation@raider.io">moderation@raider.io</a> and include:
      </p>
      <ul className={styles.ol}>
        <li>the URL (or enough detail to locate the content),</li>
        <li>a brief description of what you believe violates the Policy, and</li>
        <li>
          any relevant context (screenshots may help, but please avoid sending sensitive personal data unless
          necessary).
        </li>
      </ul>

      <p>
        We handle reports as confidentially as reasonably possible and may contact you if we need additional
        information. Submitting false, abusive, or harassing reports may result in enforcement action.
      </p>

      <h3>Response targets</h3>
      <p>
        We aim to review reports as promptly as practicable, typically within 24–48 hours. More severe reports (e.g.,
        threats, child safety, malware/phishing) may be prioritized.
      </p>

      <h2>6. Enforcement and Consequences</h2>

      <p>
        We may take action on UGC or accounts at any time, with or without notice, including when we believe content
        violates this Policy or creates safety/brand-safety risk.
      </p>

      <p>Possible actions include (without limitation):</p>
      <ul className={styles.ol}>
        <li>removing or editing content (or limiting its visibility),</li>
        <li>issuing warnings,</li>
        <li>restricting access to specific UGC features,</li>
        <li>temporarily suspending accounts (including feature-level suspensions), and/or</li>
        <li>permanently banning accounts.</li>
      </ul>

      <h3>Graduated enforcement (typical)</h3>
      <ul className={styles.ol}>
        <li>First violation (low severity): content removal and/or warning.</li>
        <li>Repeated violations: temporary suspension (commonly 7–30 days).</li>
        <li>Serious or repeated violations: permanent ban.</li>
        <li>
          Severe violations: immediate action up to permanent ban and, where appropriate, referral to law enforcement or
          relevant authorities.
        </li>
      </ul>

      <p>
        We may preserve content and related records as needed for investigations, enforcement, safety, or legal
        compliance.
      </p>

      <h2>7. Appeals</h2>

      <p>
        Users may appeal certain moderation decisions by emailing{' '}
        <a href="mailto:moderation@raider.io">moderation@raider.io</a> within 30 days of the action. Please include:
      </p>
      <ul className={styles.ol}>
        <li>your account identifier (and relevant character/guild/team identifiers, if applicable),</li>
        <li>the content at issue (URL or details),</li>
        <li>why you believe the decision was incorrect, and</li>
        <li>any relevant context.</li>
      </ul>

      <p>
        We aim to review appeals in a reasonable timeframe (typically within 14 days). Decisions on appeals are made at
        RaiderIO's discretion. Some actions may be non-appealable where required for safety, legal compliance, or to
        prevent abuse.
      </p>

      <h2>8. Changes to this Policy</h2>

      <p>
        We may update this Policy from time to time. The "Last updated" date indicates when changes were made. Continued
        use of the Site after changes become effective constitutes acceptance of the updated Policy.
      </p>

      <h2>9. Contact</h2>

      <p>
        For questions or concerns about this Policy, contact{' '}
        <a href="mailto:moderation@raider.io">moderation@raider.io</a> or write to:
      </p>
      <p>
        RaiderIO
        <br />
        1887 Whitney Mesa Dr #4968
        <br />
        Henderson, NV 89014
      </p>
    </div>
  );
}
